import type { ReactNode } from 'react'

import { AuthBackdrop } from '@/components/ui/auth-backdrop'
import { LockIcon } from '@/components/ui/icons'
import { Logo, LogoWatermark } from '@/components/ui/logo'
import { site } from '@/config/site'

interface AuthLayoutProps {
  title: string
  subtitle?: ReactNode
  children: ReactNode
  footer?: ReactNode
}

export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    // `auth-scope` re-points HeroUI's accent and field tokens to the brand palette;
    // `light` pins the card to light-theme tokens so the white surface survives a
    // dark-mode viewer, matching the single fixed look of the design.
    <div className="auth-scope light relative flex min-h-dvh flex-col overflow-hidden bg-[#5D178C] text-white">
      <AuthBackdrop className="pointer-events-none absolute inset-0" />
      {/* Flush to the corner at the design's 33.2% width — the export is pre-cropped. */}
      <LogoWatermark className="pointer-events-none absolute right-0 bottom-0 w-[33.2%] max-w-[574px] min-w-[300px] select-none" />

      <header className="relative z-10 flex items-center justify-between gap-4 px-6 pt-6 sm:px-10 sm:pt-8 lg:px-16 lg:pt-12">
        <Logo className="h-8 w-auto text-white sm:h-9 lg:h-11" />
        <span className="text-xs font-light tracking-wide text-white/80 sm:text-sm">
          {site.portalName}
        </span>
      </header>

      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-5 py-10 sm:px-6">
        <div className="auth-rise w-full max-w-[420px]">
          <div className="mb-7 text-center">
            <h1 className="text-[28px] leading-tight font-bold tracking-[-0.02em] sm:text-[34px]">
              {title}
            </h1>
            {subtitle && (
              // 4.78:1 on the lightest part of the wash — the subtitle sits where the
              // radial is brightest, so it needs more white than the rest of the chrome.
              <p className="mx-auto mt-2.5 max-w-[36ch] text-sm leading-relaxed text-white/85">
                {subtitle}
              </p>
            )}
          </div>

          {/* Two-stop shadow: a wide ambient cast for depth plus a tight contact
              shadow, so the card sits on the wash instead of floating over it. */}
          <div className="bg-surface text-foreground rounded-[20px] p-6 shadow-[0_28px_64px_-20px_rgba(28,5,52,0.62),0_3px_10px_-3px_rgba(28,5,52,0.4)] ring-1 ring-black/[0.06] sm:p-7">
            {children}
            {footer && <div className="mt-5 border-t border-black/[0.07] pt-4">{footer}</div>}
          </div>

          <p className="mt-5 flex items-center justify-center gap-1.5 text-xs text-white/80">
            <LockIcon width={14} height={14} aria-hidden />
            Encrypted connection
          </p>
        </div>
      </main>

      <footer className="relative z-10 px-6 pb-7 text-center text-[11px] tracking-wide text-white/75">
        © {new Date().getFullYear()} {site.shortName} · {site.tagline} · {site.location}
      </footer>
    </div>
  )
}
